import { useEffect, useState, useCallback, useMemo } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";

const ICON_COLORS = ["#3b82f6", "#8b5cf6", "#06b6d4", "#f59e0b", "#10b981", "#ec4899"];

const getProjectInitial = (project, isStudent) => {
  const source = isStudent
    ? (project.createdByInstitute || project.title || "P")
    : (project.title || "P");
  return source.charAt(0).toUpperCase();
};

const STATUS_STYLES = {
  applied: { bg: "#eff6ff", color: "#1d4ed8", label: "Applied" },
  shortlisted: { bg: "#f0fdf4", color: "#15803d", label: "Shortlisted" },
  selected: { bg: "#f0fdf4", color: "#15803d", label: "Selected" },
  rejected: { bg: "#fef2f2", color: "#b91c1c", label: "Rejected" },
  pending: { bg: "#fefce8", color: "#92400e", label: "Pending" },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const [applications, setApplications] = useState([]);

  const fetchProjects = useCallback(async (uid, userRole) => {
    let q;
    if (userRole === "professor") {
      q = query(collection(db, "projects"), where("createdBy", "==", uid));
    } else {
      q = query(collection(db, "projects"), where("publishingStatus", "==", "approved"));
    }
    const snapshot = await getDocs(q);
    let list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (userRole !== "professor") {
      list = list.filter((p) => p.status === "open");
    }
    setProjects(list.slice(0, 4));
  }, []);

  const fetchApplications = useCallback(async (uid, userRole) => {
    if (!uid) return;
    let q;
    if (userRole === "professor") {
      q = query(
        collection(db, "applications"),
        where("professorId", "==", uid),
        where("adminApprovalStatus", "==", "approved")
      );
    } else {
      q = query(
        collection(db, "applications"),
        where("studentId", "==", uid)
      );
    }
    const snapshot = await getDocs(q);
    const apps = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    // Fetch project titles for each application
    const enriched = await Promise.all(
      apps.map(async (app) => {
        if (app.projectId) {
          try {
            const projSnap = await getDoc(doc(db, "projects", app.projectId));
            if (projSnap.exists()) {
              return { ...app, projectTitle: projSnap.data().title, projectHost: projSnap.data().createdByName };
            }
          } catch { /* ignore */ }
        }
        return app;
      })
    );
    setApplications(enriched.slice(0, 5));
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      try {
        setLoading(true);
        const userRef = doc(db, "users", user.uid);
        const snapshot = await getDoc(userRef);
        let currentRole = null;
        if (snapshot.exists()) {
          const data = snapshot.data();
          setName(data.fullName || "");
          setRole(data.role || null);
          currentRole = data.role;
        }
        await fetchProjects(user.uid, currentRole);
        await fetchApplications(user.uid, currentRole);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [fetchProjects, fetchApplications]);

  const isStudent = role === "student";

  const filteredProjects = useMemo(() => {
    if (!search.trim()) return projects;
    const q = search.toLowerCase();
    return projects.filter(
      (p) =>
        p.title?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.location?.toLowerCase().includes(q) ||
        (isStudent && p.createdByInstitute?.toLowerCase().includes(q)) ||
        (!isStudent && p.publishingStatus?.toLowerCase().includes(q))
    );
  }, [projects, search, isStudent]);

  useEffect(() => {
    if (window.location.hash === "#applications") {
      document.getElementById("applications")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [loading]);

  return (
    <AppShell
      searchValue={search}
      onSearchChange={setSearch}
      searchPlaceholder={isStudent ? "Search projects, universities..." : "Search your projects..."}
    >
      {loading ? (
        <div className="home-shimmer">
          <div className="home-shimmer-block" style={{ height: 180 }} />
          <div className="home-shimmer-block" />
          <div className="home-shimmer-block" />
        </div>
      ) : (
        <div className="home-page">
          <section className="home-hero">
            <div className="home-hero-text">
              {!isStudent ? (
                <>
                  <h1>Welcome to FEP Host Portal</h1>
                  <p>
                    {name ? ` Welcome back, Professor ${name.split(" ")[0]}. ` : ""}
                    Manage your research projects, review student applications, and track project progress.
                  </p>
                  <div className="home-hero-actions" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="home-btn-primary"
                      onClick={() => navigate("/dashboard/projects?action=create")}
                    >
                      Create New Project
                    </button>
                    <button
                      type="button"
                      className="home-btn-secondary"
                      onClick={() => navigate("/dashboard/projects")}
                      style={{ background: 'rgba(255, 255, 255, 0.15)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.25)', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', transition: 'background 0.2s' }}
                      onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.25)'}
                      onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.15)'}
                    >
                      Manage Projects
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h1>Welcome to FEP Portal</h1>
                  <p>
                    Discover research opportunities, connect with partner universities,
                    and manage your foreign exposure journey.
                    {name ? ` Glad to have you here, ${name.split(" ")[0]}.` : ""}
                  </p>
                  <div className="home-hero-actions">
                    <button
                      type="button"
                      className="home-btn-primary"
                      onClick={() => navigate("/dashboard/projects")}
                    >
                      Browse Projects
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="home-hero-art" aria-hidden="true">
              <div className="home-hero-block" />
              <div className="home-hero-block" />
              <div className="home-hero-block" />
              <div className="home-hero-block" />
            </div>
          </section>

          <div className="home-grid">
            <section>
              <div className="home-section-header">
                <h2>
                  {isStudent ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M23 6l-9.5 9.5-5-5L1 18" />
                        <path d="M17 6h6v6" />
                      </svg>
                      Trending Opportunities
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                        <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
                      </svg>
                      My Projects
                    </>
                  )}
                </h2>
                <button
                  type="button"
                  className="home-section-link"
                  onClick={() => navigate("/dashboard/projects")}
                >
                  View all
                </button>
              </div>

              <div className="home-trending-grid">
                {filteredProjects.length === 0 ? (
                  <p className="home-empty">
                    {isStudent ? "No open projects right now. Check back soon." : "You haven't created any projects yet."}
                  </p>
                ) : (
                  filteredProjects.map((project, i) => (
                    <button
                      key={project.id}
                      type="button"
                      className="home-project-card"
                      onClick={() => navigate("/dashboard/projects")}
                    >
                      <div
                        className="home-project-icon"
                        style={{ background: ICON_COLORS[i % ICON_COLORS.length] }}
                      >
                        {getProjectInitial(project, isStudent)}
                      </div>
                      <div className="home-project-body">
                        <h3>{project.title}</h3>
                        {isStudent ? (
                          <p className="home-project-org">
                            {project.createdByInstitute || project.location || "IIT Kanpur"}
                          </p>
                        ) : (
                          <p className="home-project-org" style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px", margin: "4px 0 10px" }}>
                            <span style={{ color: project.status === "open" ? "#16a34a" : "#dc2626" }}>
                              {project.status || "open"}
                            </span>
                            <span style={{ color: "#94a3b8", margin: "0 6px" }}>•</span>
                            <span style={{ color: "#475569" }}>
                              Publishing: {project.publishingStatus || "pending"}
                            </span>
                          </p>
                        )}
                        <div className="home-project-tags">
                          {project.compensation && (
                            <span className="home-project-tag">{project.compensation}</span>
                          )}
                          {project.timeCommitment && (
                            <span className="home-project-tag">{project.timeCommitment}</span>
                          )}
                          {project.location && (
                            <span className="home-project-tag">{project.location}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </section>

            <aside className="home-events-panel" id="applications">
              <div className="home-section-header">
                <h2>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  {isStudent ? "My Applications" : "Recent Applications"}
                </h2>
                <button
                  type="button"
                  className="home-section-link"
                  onClick={() => navigate("/dashboard/projects")}
                >
                  {isStudent ? "Apply now" : "Manage applications"}
                </button>
              </div>

              <div className="home-events-list">
                {applications.length === 0 ? (
                  <div className="home-event-item">
                    <p className="home-event-venue">
                      {isStudent ? "No applications yet. Browse projects and apply!" : "No applications received yet."}
                    </p>
                  </div>
                ) : (
                  applications.map((app) => {
                    const s = STATUS_STYLES[app.status] || STATUS_STYLES.pending;
                    return (
                      <div
                        key={app.id}
                        className="home-event-item home-app-item"
                        onClick={() => navigate(`/dashboard/applications/${app.id}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <h4 style={{ margin: "0 0 4px" }}>
                          {isStudent ? (app.projectTitle || "Untitled Project") : (app.studentName || "Anonymous Student")}
                        </h4>
                        <p className="home-event-date" style={{ margin: "0 0 6px" }}>
                          {isStudent ? (app.projectHost || "") : (app.projectTitle || "")}
                        </p>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 10px",
                            borderRadius: "999px",
                            fontSize: "11px",
                            fontWeight: 600,
                            background: s.bg,
                            color: s.color,
                          }}
                        >
                          {s.label}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="home-help-card">
                <h3>Need Help?</h3>
                <p>
                  Contact the FEP team for queries about applications, eligibility,
                  or partner universities.
                </p>
                <a href="mailto:fepiitk@gmail.com">Contact Support →</a>
              </div>
            </aside>
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default Dashboard;